import { ReactNode } from "react";
import { FOCUS_RING, cx } from "./tokens";

export interface FilterRailSection {
  id: string;
  label: string;
  icon: ReactNode;
  /** Options available in this section. */
  total: number;
  /** Options currently chosen. */
  selected: number;
}

export interface FilterRailProps {
  sections: FilterRailSection[];
  activeId: string;
  onSelect: (id: string) => void;
  className?: string;
}

/**
 * Section navigation with a sense of scale before a section is opened.
 *
 * Counts read `18` when nothing is chosen and `3/18` once a selection exists,
 * so a bare number is never ambiguous between available and selected (§23.8).
 */
export default function FilterRail({
  sections,
  activeId,
  onSelect,
  className,
}: FilterRailProps) {
  return (
    <nav className={cx("flex flex-col gap-0.5", className)} aria-label="Seções do filtro">
      {sections.map((section) => {
        const active = section.id === activeId;
        const hasSelection = section.selected > 0;

        return (
          <button
            key={section.id}
            type="button"
            onClick={() => onSelect(section.id)}
            aria-current={active ? "true" : undefined}
            className={cx(
              "flex items-center gap-2.5 rounded-chip px-3 py-2 text-left text-[13px]",
              "transition-colors duration-150",
              "[&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:shrink-0",
              FOCUS_RING,
              active
                ? "bg-select/[0.12] font-semibold text-select-ink"
                : "font-medium text-ink-secondary hover:bg-surface-hover hover:text-ink",
            )}
          >
            {section.icon}
            <span className="min-w-0 flex-1 truncate">{section.label}</span>
            <span
              className={cx(
                "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums",
                hasSelection
                  ? "bg-select/[0.14] text-select-ink"
                  : "bg-surface-muted text-ink-muted",
              )}
              aria-label={
                hasSelection
                  ? `${section.selected} de ${section.total} selecionados`
                  : `${section.total} disponíveis`
              }
            >
              {hasSelection ? `${section.selected}/${section.total}` : section.total}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
